import Joi from 'joi';

/**
 * createNoteLayout — single source of truth for label + control type + maxLength per field.
 * Used by CreateNoteDlgBody (FieldRow) AND createNoteSchema (Joi .label()).
 * maxLength mirrors Joi .max() — single source, no drift.
 */
export const createNoteLayout = () => ({
    title: { label: 'Title', control: 'text', maxLength: 200 },
    service: { label: 'Service', control: 'text', readonly: true },
    cat: { label: 'Cat', control: 'text', maxLength: 100 },
    type: { label: 'Type', control: 'text', maxLength: 50 },
    stage: { label: 'Stage', control: 'text', maxLength: 50 },
    importanceId: { label: 'Importance', control: 'select' },
    prioId: { label: 'Prio', control: 'select' },
    format: { label: 'Format', control: 'text', maxLength: 50 },
    viewMode: { label: 'ViewMode', control: 'text', maxLength: 50 },
    tid: { label: 'Template', control: 'text', readonly: true },
});

export const ImportanceOptions = [
    { label: 'Unknown', value: 0 },
    { label: 'Low', value: 100 },
    { label: 'Medium', value: 500 },
    { label: 'High', value: 900 },
];

/**
 * createDlgConfig — JxDialogState config for CreateNoteDlgWidget.
 * focusFieldId targets the title field so it's focused on open.
 * Name matches getId() convention: {this.name}_{id} — default id is '01'.
 */
export const createDlgConfig = () => ({
    // be aware that id is component name + 01 + field name
    focusFieldId: 'CreateNoteDlgBody_01_title',
});

/**
 * createEmpty — default shape for a new note, optionally seeded from anchorEntry.
 * @param {object|null} anchorEntry
 */
export const createEmpty = (anchorEntry = null) => ({
    title: anchorEntry?.title ?? '',
    service: anchorEntry?.service ?? '',
    cat: anchorEntry?.cat ?? '',
    type: anchorEntry?.type ?? '',
    stage: anchorEntry?.stage ?? '',
    importanceId: anchorEntry?.importanceId ?? 0,
    prioId: anchorEntry?.prioId ?? 0,
    format: anchorEntry?.format ?? '',

    viewMode: anchorEntry?.viewMode ?? '',

    tid: anchorEntry?.tid ?? '',
});

/**
 * createSchema — Joi validation. Labels from createNoteLayout so display
 * label and validation label can't drift apart.
 */
export const createSchema = () => {
    const layout = createNoteLayout();

    return {
        title: Joi.string().max(200).required().label(layout.title.label),
        service: Joi.string().max(100).required().label(layout.service.label),
        cat: Joi.string().max(100).allow(null, '').label(layout.cat.label),
        type: Joi.string().max(50).allow(null, '').label(layout.type.label),
        stage: Joi.string().max(50).allow(null, '').label(layout.stage.label),
        importanceId: Joi.number().label(layout.importanceId.label),
        prioId: Joi.number().label(layout.prioId.label),
        format: Joi.string().max(50).allow(null, '').label(layout.format.label),

        viewMode: Joi.string().max(50).allow(null, '').label(layout.viewMode.label),

        tid: Joi.string().max(36).allow(null, '').label(layout.tid.label),
    };
};
